import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { StateChip } from '@/components/submission/FamilyPicker';
import { PlatformModeSwitch } from '@/components/PlatformModeSwitch';
import { Download, RefreshCw, Archive, Lock, ShieldCheck } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', slate: '#5C6B7A' };
const fmt = (v) => `$${Math.round(v || 0).toLocaleString('en-AU')}`;
const fmtM = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(2)}m` : v >= 1e3 ? `$${Math.round(v / 1e3)}k` : fmt(v);

function BookShape({ byFamily, book }) {
  const max = Math.max(1, ...byFamily.map(f => f.gwp));
  return (
    <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="book-by-family">
      <div className="flex items-baseline justify-between mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.blue }}>The book by family</p>
        <p className="text-xs" style={{ color: C.slate }}>{book.policies.toLocaleString()} live policies · {fmtM(book.gwp)} GWP{book.unmapped_active ? ` · ${book.unmapped_active} unmapped` : ''}</p>
      </div>
      <div className="space-y-2.5">
        {byFamily.map(f => (
          <div key={f.family} className="flex items-center gap-3" data-testid={`book-family-${f.family}`}>
            <span className="w-5 text-[10px] font-mono font-bold" style={{ color: C.blue }}>{f.number}</span>
            <span className="w-56 text-xs truncate" style={{ color: C.navy }}>{f.name}</span>
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
              <div className="h-full rounded-full" style={{ width: `${(f.gwp / max) * 100}%`, background: C.blue }} />
            </div>
            <span className="w-20 text-right text-xs font-mono" style={{ color: C.navy }}>{fmtM(f.gwp)}</span>
            <span className="w-16 text-right text-[10px]" style={{ color: C.slate }}>{f.policies.toLocaleString()} pol.</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function StateEditor({ v, onChanged }) {
  const api = useApi();
  const [state, setState] = useState(v.state);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/classes/${v.class_id}/state`, { state, reason });
      toast.success(`${v.name}: ${data.was} → ${data.state}`);
      setReason('');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change the state');
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`state-editor-${v.class_id}`}>
      <select value={state} onChange={e => setState(e.target.value)} data-testid={`state-select-${v.class_id}`}
        className="h-8 text-xs rounded-sm px-2 bg-white" style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy }}>
        {['active', 'disabled', 'run-off'].map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required, audited)" data-testid={`state-reason-${v.class_id}`}
        className="h-8 text-xs rounded-sm flex-1 min-w-[200px]" />
      <Button size="sm" onClick={save} disabled={busy || state === v.state || reason.trim().length < 5} data-testid={`state-save-${v.class_id}`}
        className="h-8 rounded-sm text-xs" style={{ background: C.blue }}>Apply</Button>
    </div>
  );
}

function VariantRow({ v, canEdit, onChanged }) {
  const api = useApi();
  const blockers = (v.blockers || []).filter(b => !['class_disabled', 'class_runoff'].includes(b.code));
  const exportConfig = async () => {
    const { data } = await api.get(`/classes/${v.class_id}/export`);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${v.class_id}-configuration.json`;
    a.click();
  };
  return (
    <div className="py-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`register-variant-${v.class_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold" style={{ color: v.state === 'active' ? C.navy : C.slate }}>{v.label}</p>
        <span className="text-[10px] font-mono" style={{ color: C.slate }}>{v.class_id}</span>
        <StateChip state={v.state} />
        {v.quotable
          ? <span className="text-[10px] font-semibold flex items-center gap-1" style={{ color: C.green }}><ShieldCheck className="w-3 h-3" />quotable{v.broker_visible ? ' · offered to brokers' : ''}</span>
          : <span className="text-[10px] font-semibold" style={{ color: v.state === 'active' ? C.red : C.slate }}>not offered</span>}
        <span className="ml-auto text-xs font-mono" style={{ color: C.navy }} data-testid={`register-count-${v.class_id}`}>{v.policy_count.toLocaleString()} pol. · {fmtM(v.gwp)}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px]" style={{ color: C.slate }}>
        {v.wording_version ? <span>Wording v{v.wording_version}</span> : <span style={{ color: C.red }}>No active wording</span>}
        {v.state_basis && <span>{v.state_basis}</span>}
        {v.runoff && (
          <span className="flex items-center gap-1" data-testid={`runoff-terms-${v.class_id}`}>
            <Archive className="w-3 h-3" />
            Entered run-off {v.runoff.entered_at} · notice period ends {v.runoff.notice_end}
            {v.runoff.renewals_open ? ` (${v.runoff.days_of_notice_left} days — renewals proceed)` : ' (closed — non-renewal notices issue)'}
            {v.runoff.expiry_horizon ? ` · expiry horizon ${v.runoff.expiry_horizon}` : ''}
            {v.runoff.notices_issued ? ` · ${v.runoff.notices_issued} notice(s) issued` : ''}
          </span>
        )}
        {v.state === 'disabled' && <span className="flex items-center gap-1"><Lock className="w-3 h-3" />Policies renew, endorse, cancel and claim as before{v.expiry_horizon ? ` · last expiry ${v.expiry_horizon}` : ''}</span>}
        {blockers.map(b => <span key={b.code} style={{ color: C.red }}>{b.detail}</span>)}
      </div>
      <div className="mt-2 flex items-center gap-2">
        {v.state === 'disabled' && (
          <button type="button" onClick={exportConfig} data-testid={`export-config-${v.class_id}`}
            className="text-[11px] font-semibold flex items-center gap-1 hover:underline" style={{ color: C.blue }}>
            <Download className="w-3 h-3" /> Export configuration
          </button>
        )}
      </div>
      {canEdit && <StateEditor v={v} onChanged={onChanged} />}
    </div>
  );
}

export default function FamilyRegisterPage() {
  const api = useApi();
  const { user } = useAuth();
  const canEdit = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/families/register').then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Skeleton className="h-64 rounded-sm" />;
  if (!data) return <p className="text-sm" style={{ color: C.slate }}>The family register could not be loaded.</p>;

  const states = data.states || {};
  return (
    <div className="space-y-5" data-testid="family-register-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold" style={{ color: C.navy, fontFamily: "'IBM Plex Sans'" }}>Six policy families on four engines</h2>
          <p className="text-xs mt-1" style={{ color: C.slate }} data-testid="register-state-summary">
            {states.active?.length || 0} active · {states.disabled?.length || 0} disabled (configuration kept) · {states['run-off']?.length || 0} in run-off
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PlatformModeSwitch compact />
          <Button variant="outline" size="sm" onClick={load} className="rounded-sm text-xs" data-testid="register-refresh-btn">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      <BookShape byFamily={data.by_family} book={data.book} />

      {data.families.map(f => (
        <Card key={f.id} className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: `3px solid ${C.blue}` }} data-testid={`register-family-${f.id}`}>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
            <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: C.slate }}>Family {f.number}</span>
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>{f.name}</h3>
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }}>{f.engine_label}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(136,150,168,0.1)', color: C.slate }}>{f.trigger_label}</span>
            <span className="text-[10px]" style={{ color: C.slate }}>{f.priority_label}</span>
            <span className="ml-auto text-xs" style={{ color: C.slate }}>{f.quotable_count}/{f.total} quotable · {f.policies.toLocaleString()} policies · {fmtM(f.gwp)}</span>
          </div>
          <p className="text-xs mb-2" style={{ color: C.slate }}>{f.description}</p>
          {f.variants.map(v => <VariantRow key={v.class_id} v={v} canEdit={canEdit} onChanged={load} />)}
        </Card>
      ))}
    </div>
  );
}
